import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, X } from 'lucide-react';

// FECOA-1920. Section names are the exporter's payload keys; they are readable
// enough to leak into an operator-facing screen, but only just, so they get
// proper labels here. Anything unmapped falls back to the raw key rather than
// being hidden, since a new section appearing is itself worth seeing.
const SECTION_LABELS = {
  study: 'Study',
  mfa_settings: 'MFA settings',
  survey: 'Surveys',
  assessment: 'Assessments',
  question: 'Questions',
  layout_template: 'Question layout templates',
  screen_layout_template: 'Screen layout templates',
  assessment_template: 'Assessment templates',
  screen: 'Screens',
  screen_question: 'Screen questions',
  assessment_question: 'Assessment questions',
  navigation_rule: 'Navigation rules',
  scoring_rule: 'Scoring rules',
  form_variant: 'Form variants',
  schedule: 'Schedules',
  schedule_version: 'Schedule versions',
  reminder_template: 'Reminder templates',
  device: 'Devices',
  screen_template: 'Screen templates',
  vpg_question: 'VPG library questions',
};

const STATUS_TEXT = {
  match: 'Matches',
  mismatch: 'Differs from the candidate',
  missing_in_target: 'Missing in this environment',
  unexpected_in_target: 'Present here but not in the candidate',
};

const sectionLabel = (name) => SECTION_LABELS[name] || name;

const RESULT_HEADINGS = {
  match: {
    title: 'Target matches the candidate',
    body: 'Every section re-exported from this environment hashes identically to the candidate that was deployed.',
    classes: 'border-success-600/15 bg-success-50 dark:border-success-400/25 dark:bg-success-500/10',
  },
  mismatch: {
    title: 'Target has drifted from the candidate',
    body: 'At least one section no longer matches what was deployed. The most common cause is a hand edit made in this environment after the deploy.',
    classes: 'border-danger-600/15 bg-danger-50 dark:border-danger-400/25 dark:bg-danger-500/10',
  },
  unverifiable: {
    title: 'Nothing could be verified',
    body: 'This deployment cannot be compared against its candidate.',
    classes: 'border-warning-600/20 bg-warning-50 dark:border-warning-400/25 dark:bg-warning-500/10',
  },
};

const ResultIcon = ({ result }) => {
  if (result === 'match') return <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-success-600 dark:text-success-400" />;
  if (result === 'mismatch') return <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-danger-600 dark:text-danger-400" />;
  return <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-warning-600 dark:text-warning-400" />;
};

const VerificationReportView = ({ verification, onClose }) => {
  if (!verification) return null;

  const heading = RESULT_HEADINGS[verification.result] || RESULT_HEADINGS.unverifiable;
  const sections = verification.sections || [];

  return (
    <div className="card space-y-4 p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <ResultIcon result={verification.result} />
          <div>
            <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">
              {heading.title}
            </h3>
            <p className="text-[13px] text-gray-500 dark:text-gray-400">
              Verified {new Date(verification.verified_at).toLocaleString()}
              {verification.verified_by_email ? ` by ${verification.verified_by_email}` : ''}
            </p>
          </div>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close verification report"
            className="-mr-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        )}
      </div>

      <div className={`rounded-lg border px-4 py-3 text-[13px] text-gray-700 dark:text-gray-200 ${heading.classes}`}>
        <p>{heading.body}</p>
        {verification.detail && (
          <p className="mt-2 text-gray-600 dark:text-gray-400">{verification.detail}</p>
        )}
      </div>

      {sections.length > 0 && (
        <div className="-mx-5 overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-y border-gray-100 dark:border-white/[0.06]">
                <th className="th-cell">Section</th>
                <th className="th-cell">Result</th>
                <th className="th-cell">Content checksum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 border-b border-gray-100 dark:divide-white/[0.06] dark:border-white/[0.06]">
              {sections.map((row) => {
                const passed = row.status === 'match';
                return (
                  <tr key={row.name}>
                    <td className="td-cell text-gray-900 dark:text-gray-50">
                      {sectionLabel(row.name)}
                    </td>
                    <td className="td-cell">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium ${passed ? 'text-success-700 dark:text-success-400' : 'text-danger-700 dark:text-danger-400'}`}>
                        {passed
                          ? <CheckCircle className="h-3.5 w-3.5" />
                          : <XCircle className="h-3.5 w-3.5" />}
                        {STATUS_TEXT[row.status] || row.status}
                      </span>
                    </td>
                    <td className="td-cell font-mono text-xs text-gray-500 dark:text-gray-400">
                      {passed ? (
                        <span>{(row.expected || '').slice(0, 12)}…</span>
                      ) : (
                        <span className="block">
                          <span className="block">
                            candidate: {row.expected ? `${row.expected.slice(0, 12)}…` : 'absent'}
                          </span>
                          <span className="block">
                            here: {row.actual ? `${row.actual.slice(0, 12)}…` : 'absent'}
                          </span>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {verification.expected_payload_checksum && (
        <p className="break-all font-mono text-xs text-gray-500 dark:text-gray-400">
          Whole-package checksum: candidate {verification.expected_payload_checksum.slice(0, 16)}…, here{' '}
          {(verification.actual_payload_checksum || '').slice(0, 16)}…
        </p>
      )}
    </div>
  );
};

export default VerificationReportView;
